// Everything the app knows about a job lives here, on the device (IndexedDB),
// before it lives anywhere else. docs/OFFLINE.md: edits always go to the device
// first; nothing is held only in memory.
//
// This file is what screens do: read a job, save an edit, save a photo. The
// database itself is device-store.ts; sync's bookkeeping is device-sync.ts.
// Each edit writes the record AND its outbox entry in one transaction, so a
// change can never be saved without also being queued to sync (or vice versa).
import type { Direction } from './compass';
import { idsWithWork, isAreaHeld, mayForgetRemovedArea } from './removed-areas';
import {
  db,
  isChange,
  notifyChange,
  queue,
  type DeviceJob,
  type DevicePhoto,
  type DeviceRecord,
  type Fields,
  type OutboxEntry,
  type ReferenceLists,
  type SitePlanImage,
} from './device-store';

export { isChange, onDeviceChange } from './device-store';
export type { DeviceJob, DevicePhoto, DeviceRecord, DeviceTable, Fields, OutboxEntry } from './device-store';

// ---------- reads ----------

export async function listJobs(): Promise<DeviceJob[]> {
  return (await db()).getAll('jobs');
}

export async function getJob(jobId: string): Promise<DeviceJob | undefined> {
  return (await db()).get('jobs', jobId);
}

export async function listRecords(jobId: string): Promise<DeviceRecord[]> {
  return (await db()).getAllFromIndex('records', 'byJob', jobId);
}

export async function getRecord(id: string): Promise<DeviceRecord | undefined> {
  return (await db()).get('records', id);
}

/** An area's photos in name order (General 01, General 02 …), not storage order. */
export async function listPhotos(areaId: string): Promise<DevicePhoto[]> {
  const photos = await (await db()).getAllFromIndex('photos', 'byArea', areaId);
  return photos.sort((a, b) => a.fileName.localeCompare(b.fileName, undefined, { numeric: true }));
}

/** Every photo of a job on this device (each area's screens pick their own: one read per job, not per area). */
export async function listJobPhotos(jobId: string): Promise<DevicePhoto[]> {
  return (await db()).getAllFromIndex('photos', 'byJob', jobId);
}

export async function getPhoto(id: string): Promise<DevicePhoto | undefined> {
  return (await db()).get('photos', id);
}

/** Changes still to send (a job's, or every job's). A queued "opened" isn't one. */
export async function countWaiting(jobId?: string): Promise<number> {
  const database = await db();
  const entries = jobId ? await database.getAllFromIndex('outbox', 'byJob', jobId) : await database.getAll('outbox');
  return entries.filter(isChange).length;
}

/** The outbox in the order changes were made. */
export async function listOutbox(): Promise<OutboxEntry[]> {
  return (await db()).getAll('outbox');
}

/** A reference list last copied from CRM; empty if this device never had signal to fetch it. */
export async function getReferenceList<K extends keyof ReferenceLists>(key: K): Promise<ReferenceLists[K]> {
  return ((await (await db()).get('reference', key))?.[key] ?? []) as ReferenceLists[K];
}

/** A visit's site plan image on this device, if it has one. */
export async function getSitePlanImage(visitId: string): Promise<SitePlanImage | undefined> {
  return (await db()).get('siteplans', visitId);
}

export async function saveSitePlanImage(image: SitePlanImage): Promise<void> {
  await (await db()).put('siteplans', image);
  notifyChange();
}

export async function saveReferenceList<K extends keyof ReferenceLists>(key: K, list: ReferenceLists[K]): Promise<void> {
  await (await db()).put('reference', { key, [key]: list, fetchedAt: new Date().toISOString() });
  notifyChange();
}

// ---------- writes ----------

/**
 * Store a job as CRM has it. Records with unsynced edits or a conflict on
 * this device are kept as they are — only a clean copy is replaced, so a
 * refresh (which runs by itself when a job opens) never discards the
 * measurer's own version.
 */
export async function saveJobSnapshot(job: Omit<DeviceJob, 'openedAt' | 'lastSyncedAt'>, records: Omit<DeviceRecord, 'jobId' | 'state' | 'revision'>[]): Promise<void> {
  const tx = (await db()).transaction(['jobs', 'records'], 'readwrite');
  const existingJob = await tx.objectStore('jobs').get(job.id);
  await tx.objectStore('jobs').put({
    ...job,
    openedAt: new Date().toISOString(),
    lastSyncedAt: existingJob?.lastSyncedAt ?? null,
    ...(existingJob?.lastOpenedAt ? { lastOpenedAt: existingJob.lastOpenedAt } : {}),
  });
  for (const incoming of records) {
    const local = await tx.objectStore('records').get(incoming.id);
    if (local && local.state !== 'synced') continue;
    await tx.objectStore('records').put({ ...incoming, jobId: job.id, state: 'synced', revision: local?.revision ?? 0 });
  }
  await tx.done;
  notifyChange();
}

/** The job was opened on this device: note when, and queue it for the Recent jobs list on every device (#422). */
export async function recordOpen(jobId: string): Promise<void> {
  const tx = (await db()).transaction(['jobs', 'outbox'], 'readwrite');
  const job = await tx.objectStore('jobs').get(jobId);
  if (job) {
    await tx.objectStore('jobs').put({ ...job, lastOpenedAt: new Date().toISOString() });
    await queue(tx, { kind: 'opened', targetId: jobId, jobId });
  }
  await tx.done;
  notifyChange();
}

/**
 * Save an edit (or a new record) on the device and queue it — one transaction.
 * The etag is sync's to change, not an edit's: a screen may still hold the
 * etag from before the last sync, and writing that back would make the next
 * sync look like a conflict with itself.
 */
export async function saveRecord(record: Omit<DeviceRecord, 'state' | 'revision'>): Promise<void> {
  const tx = (await db()).transaction(['records', 'outbox'], 'readwrite');
  await putRecord(tx, record);
  await tx.done;
  notifyChange();
}

/** The transaction a write runs in (device-store.ts queue's). */
type WriteTx = Parameters<typeof queue>[0];

/** saveRecord's write, inside the caller's transaction. */
async function putRecord(tx: WriteTx, record: Omit<DeviceRecord, 'state' | 'revision'>): Promise<void> {
  const local = await tx.objectStore('records').get(record.id);
  const conflicted = local?.state === 'conflict';
  await tx.objectStore('records').put({
    ...record,
    etag: local ? local.etag : record.etag,
    state: conflicted ? 'conflict' : 'pending',
    revision: (local?.revision ?? 0) + 1,
  });
  // A conflicted record waits for the measurer's choice (conflicts.ts); queued, it would only be refused again.
  if (!conflicted) await queue(tx, { kind: 'record', targetId: record.id, jobId: record.jobId });
}

/**
 * Change some fields of a record already on the device, merged into the copy
 * stored now (not the one a screen last read), and queue it — one
 * transaction. Two saves close together (a typed value, then the totals it
 * changed) can't undo each other. `changes` may be worked out from the stored
 * fields, for a field holding a whole document (an option's hig_scope).
 */
export const patchFields = (id: string, changes: Fields | ((stored: Fields) => Fields)): Promise<void> =>
  changeStored(id, (local) => ({ fields: { ...local.fields, ...(typeof changes === 'function' ? changes(local.fields) : changes) } }));

/**
 * Move a record to another parent — an area to another building — and queue
 * it, one transaction. The area's next send names its new building, and the
 * API moves it there (records.ts).
 */
export const moveRecord = (id: string, parentId: string): Promise<void> => changeStored(id, () => ({ parentId }));

/**
 * Change a record already on the device, worked out from the copy stored now,
 * and queue it — one transaction. A conflicted record keeps its flag and isn't
 * queued: it waits for the measurer's choice (conflicts.ts).
 */
async function changeStored(id: string, change: (local: DeviceRecord) => Partial<Pick<DeviceRecord, 'fields' | 'parentId'>>): Promise<void> {
  const tx = (await db()).transaction(['records', 'outbox'], 'readwrite');
  const local = await tx.objectStore('records').get(id);
  if (local) {
    const conflicted = local.state === 'conflict';
    await tx.objectStore('records').put({ ...local, ...change(local), state: conflicted ? 'conflict' : 'pending', revision: local.revision + 1 });
    if (!conflicted) await queue(tx, { kind: 'record', targetId: id, jobId: local.jobId });
  }
  await tx.done;
  if (local) notifyChange();
}

/**
 * Save a new photo on the device with its row, and queue both (the row first,
 * so it reaches CRM ahead of the upload) — one transaction, so a refresh never
 * sees the row without its photo and forgets the photo's area (#543).
 */
export async function savePhotoWithRow(row: Omit<DeviceRecord, 'state' | 'revision'>, photo: DevicePhoto): Promise<void> {
  const tx = (await db()).transaction(['records', 'photos', 'outbox'], 'readwrite');
  await putRecord(tx, row);
  await tx.objectStore('photos').put(photo);
  await queue(tx, { kind: 'photo', targetId: photo.id, jobId: photo.jobId });
  await tx.done;
  notifyChange();
}

/** A waiting photo's name was taken by another device's photo: it uploads under this one instead. */
export async function renamePhoto(photo: DevicePhoto, fileName: string): Promise<void> {
  await (await db()).put('photos', { ...photo, fileName });
  notifyChange();
}

/** Change a photo's direction. A photo still waiting carries it up with the file; an uploaded one queues the change. */
export async function savePhotoDirection(photo: DevicePhoto, direction: Direction | null): Promise<void> {
  const tx = (await db()).transaction(['photos', 'outbox'], 'readwrite');
  await tx.objectStore('photos').put({ ...photo, direction });
  if (photo.state === 'uploaded') await queue(tx, { kind: 'photo-meta', targetId: photo.id, jobId: photo.jobId });
  await tx.done;
  notifyChange();
}

/**
 * An area removed in CRM: drop it, its photos and anything queued for either.
 * Its photos stay on SharePoint. Removing an area is offered only once it has
 * synced, so nothing here is the measurer's only copy.
 */
export const forgetArea = (areaId: string): Promise<void> => dropArea(areaId, false);

/**
 * An area removed in CRM on another device, at a job refresh: dropped as
 * forgetArea does, but only once nothing of it is still to sync from here
 * (removed-areas.ts, #543) — else it stays, and its photos upload as usual.
 * Checked in the same transaction, so a photo taken meanwhile can't be lost.
 */
export const forgetRemovedArea = (areaId: string): Promise<void> => dropArea(areaId, true);

async function dropArea(areaId: string, onlyIfSettled: boolean): Promise<void> {
  const tx = (await db()).transaction(['records', 'photos', 'outbox'], 'readwrite');
  if (onlyIfSettled) {
    const area = await tx.objectStore('records').get(areaId);
    const records = area ? await tx.objectStore('records').index('byJob').getAll(area.jobId) : [];
    const work = area && idsWithWork(records, await tx.objectStore('outbox').index('byJob').getAll(area.jobId));
    const photos = await tx.objectStore('photos').index('byArea').getAll(areaId);
    // Held last of all, just before the deletes: a photo started while the area was being read keeps it too.
    if (!work || !mayForgetRemovedArea(area, { photos, records, work }) || isAreaHeld(areaId)) return tx.done;
  }
  const outbox = tx.objectStore('outbox').index('byTarget');
  const photoIds = await tx.objectStore('photos').index('byArea').getAllKeys(areaId);
  for (const targetId of [areaId, ...photoIds.map(String)]) {
    for (const key of await outbox.getAllKeys(targetId)) await tx.objectStore('outbox').delete(key);
  }
  for (const id of photoIds) await tx.objectStore('photos').delete(id);
  await tx.objectStore('records').delete(areaId);
  await tx.done;
  notifyChange();
}

/** A photo that never reached SharePoint and is being discarded (refused.ts): drop it, with anything queued for it. */
export const forgetPhoto = (id: string): Promise<void> => forgetFrom('photos', id);

/** A record removed in CRM (an option): drop it from the device, with anything still queued for it. */
export const forgetRecord = (id: string): Promise<void> => forgetFrom('records', id);

/** Drop one record or photo and anything queued for it — one transaction. */
const forgetFrom = (store: 'records' | 'photos', id: string): Promise<void> => forgetIn([store], id);

/**
 * A photo gone for good (removed here and in CRM, or removed in CRM by someone
 * else): its row, its copy on the device and anything queued for either.
 */
export const forgetPhotoAndRow = (id: string): Promise<void> => forgetIn(['records', 'photos'], id);

async function forgetIn(stores: ('records' | 'photos')[], id: string): Promise<void> {
  const tx = (await db()).transaction([...stores, 'outbox'], 'readwrite');
  for (const store of stores) await tx.objectStore(store).delete(id);
  for (const key of await tx.objectStore('outbox').index('byTarget').getAllKeys(id)) await tx.objectStore('outbox').delete(key);
  await tx.done;
  notifyChange();
}

/**
 * Remove a photo (needs no signal): hidden at once; its upload and any change
 * still waiting are dropped; its row is kept, marked, until CRM has made it
 * inactive and sent the file to the recycle bin (outbox photo-remove).
 */
export async function startPhotoRemoval(row: DeviceRecord): Promise<void> {
  const tx = (await db()).transaction(['records', 'photos', 'outbox'], 'readwrite');
  for (const key of await tx.objectStore('outbox').index('byTarget').getAllKeys(row.id)) await tx.objectStore('outbox').delete(key);
  await tx.objectStore('photos').delete(row.id);
  await tx.objectStore('records').put({ ...row, state: 'pending', removing: true });
  await queue(tx, { kind: 'photo-remove', targetId: row.id, jobId: row.jobId });
  await tx.done;
  notifyChange();
}

/**
 * CRM changed some of a record's columns itself (a visit completed or
 * validated): take them, and its new etag, onto the device's copy without
 * queuing a save — the device never writes those columns, so a save already
 * queued goes at the new etag instead of looking like a conflict.
 */
export async function takeServerChange(id: string, fields: Fields, etag: string): Promise<void> {
  const tx = (await db()).transaction(['records'], 'readwrite');
  const local = await tx.objectStore('records').get(id);
  if (local) await tx.objectStore('records').put({ ...local, fields: { ...local.fields, ...fields }, etag });
  await tx.done;
  if (local) notifyChange();
}
